import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useStore } from "../store";
import { LPA_STANDARDS, LPA_ROUTING, auditorTypeStyle } from "../data/eightDData";
import { useIncidents, useCreateEightD, useCreateLpa } from "../api/sitroom";

const inputStyle = {
  background: "#0d0f18",
  border: "1px solid rgba(255,255,255,0.1)",
  color: "#f0f2f8",
  fontFamily: "'DM Mono', monospace",
  fontSize: 11,
  width: "100%",
  borderRadius: 4,
  padding: "8px 10px",
};

const Label = ({ children }) => (
  <label
    style={{
      fontFamily: "'DM Mono', monospace",
      fontSize: 9,
      letterSpacing: "1.4px",
      color: "#9198b0",
      textTransform: "uppercase",
    }}
  >
    {children}
  </label>
);

export const NewEightDModal = () => {
  const isOpen = useStore((s) => s.isNewEightDModalOpen);
  const closeModal = useStore((s) => s.closeNewEightDModal);
  const prefillId = useStore((s) => s.newEightDPrefillIncidentId);
  const addToast = useStore((s) => s.addToast);
  const { data: incidents = [] } = useIncidents();
  const createEightD = useCreateEightD();

  const [form, setForm] = useState({
    sourceIncident: "",
    title: "",
    customer: "",
    supplier: "",
    internalRef: "",
    sourceOfIssue: ["Customer Complaint"],
    teamChampion: "",
  });

  useEffect(() => {
    if (isOpen) {
      const inc = incidents.find((i) => i.id === prefillId) || null;
      setForm({
        sourceIncident: inc?.id || "",
        title: inc?.title || "",
        customer: inc?.linkedParties?.find((p) => p.role === "OEM")?.name || "BMW Regensburg",
        supplier: inc?.linkedParties?.find((p) => p.role === "TIER 1")?.name || "",
        internalRef: "",
        sourceOfIssue: ["Customer Complaint"],
        teamChampion: "",
      });
    }
  }, [isOpen, prefillId]);

  if (!isOpen) return null;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSource = (s) => {
    setForm((f) => ({
      ...f,
      sourceOfIssue: f.sourceOfIssue.includes(s)
        ? f.sourceOfIssue.filter((x) => x !== s)
        : [...f.sourceOfIssue, s],
    }));
  };

  const handleSubmit = () => {
    createEightD.mutate({
      sourceIncident: form.sourceIncident,
      title: form.title,
      customer: form.customer,
      supplier: form.supplier,
      internalRef: form.internalRef,
      sourceOfIssue: form.sourceOfIssue,
      teamChampion: form.teamChampion,
      status: "in-progress",
      currentStep: "D1",
      progress: 1,
      lastUpdated: new Date().toISOString().slice(0, 10),
    });
    addToast({
      type: "info",
      title: "8D report created",
      body: form.title || "New 8D — proceed to D0/D1",
    });
    closeModal();
  };

  return (
    <>
      <div className="fixed inset-0 z-[700] backdrop-enter" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }} onClick={closeModal} />
      <div className="fixed z-[701] flex items-end sm:items-center justify-center pointer-events-none" style={{ inset: 0 }}>
        <div
          className="modal-enter pointer-events-auto flex flex-col"
          style={{ background: "#111420", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, width: "min(520px, 100vw)", maxHeight: "90dvh", boxShadow: "0 30px 70px rgba(0,0,0,0.6)" }}
          data-testid="new-8d-modal"
        >
          <header className="relative px-5 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
            <div style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: 16, color: "#f0f2f8" }}>
              New 8D Report
            </div>
            <button onClick={closeModal} className="absolute right-3 top-3 flex items-center justify-center rounded-md hover:bg-white/5" style={{ width: 28, height: 28, border: "1px solid rgba(255,255,255,0.1)", color: "#9aa1b3" }}>
              <X size={14} />
            </button>
          </header>
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            <div>
              <Label>Link to Incident</Label>
              <select value={form.sourceIncident} onChange={(e) => setField("sourceIncident", e.target.value)} style={inputStyle} className="mt-1">
                <option value="" style={{ background: "#0d0f18" }}>— None —</option>
                {incidents.map((i) => <option key={i.id} value={i.id} style={{ background: "#0d0f18" }}>{i.id} — {i.title}</option>)}
              </select>
            </div>
            <div><Label>Working Title</Label><input style={inputStyle} className="mt-1" value={form.title} onChange={(e) => setField("title", e.target.value)} data-testid="8d-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Customer</Label><input style={inputStyle} className="mt-1" value={form.customer} onChange={(e) => setField("customer", e.target.value)} /></div>
              <div><Label>Supplier</Label><input style={inputStyle} className="mt-1" value={form.supplier} onChange={(e) => setField("supplier", e.target.value)} /></div>
            </div>
            <div><Label>Internal Reference Nr</Label><input style={inputStyle} className="mt-1" value={form.internalRef} onChange={(e) => setField("internalRef", e.target.value)} /></div>
            <div>
              <Label>Source of Issue</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {["Customer Complaint", "Internal Manufacturing", "Product Development", "Other"].map((s) => {
                  const on = form.sourceOfIssue.includes(s);
                  return (
                    <button key={s} type="button" onClick={() => toggleSource(s)}
                      style={{
                        background: on ? "rgba(24,95,165,0.15)" : "transparent",
                        border: `1px solid ${on ? "#185FA5" : "rgba(255,255,255,0.1)"}`,
                        color: on ? "#9ec3ec" : "#9aa1b3",
                        fontFamily: "'DM Mono', monospace", fontSize: 10, padding: "4px 10px", borderRadius: 999,
                      }}
                    >{s}</button>
                  );
                })}
              </div>
            </div>
            <div><Label>Team Champion</Label><input style={inputStyle} className="mt-1" placeholder="Name + contact" value={form.teamChampion} onChange={(e) => setField("teamChampion", e.target.value)} /></div>
          </div>
          <footer className="flex items-center gap-2 px-5 py-3" style={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}>
            <button onClick={closeModal} className="px-4 py-2 rounded-md" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.1)", color: "#9aa1b3", fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 600, fontSize: 12 }}>Cancel</button>
            <div className="flex-1" />
            <button onClick={handleSubmit} className="px-4 py-2 rounded-md" style={{ background: "#185FA5", color: "#fff", fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 12 }} data-testid="submit-8d-btn">Create 8D Report</button>
          </footer>
        </div>
      </div>
    </>
  );
};

export const NewLPAModal = () => {
  const isOpen = useStore((s) => s.isNewLpaModalOpen);
  const closeModal = useStore((s) => s.closeNewLpaModal);
  const prefillId = useStore((s) => s.newLpaPrefillIncidentId);
  const addToast = useStore((s) => s.addToast);
  const { data: incidents = [] } = useIncidents();
  const createLpa = useCreateLpa();

  const [form, setForm] = useState({
    sourceIncident: "",
    standard: "VDA 6.3",
    severity: "s1",
    auditor: "",
    scheduledDate: "",
    scope: "",
    email: true,
    inApp: true,
    overrideType: false,
  });

  useEffect(() => {
    if (isOpen) {
      const inc = incidents.find((i) => i.id === prefillId) || null;
      const d = new Date();
      d.setDate(d.getDate() + 14);
      setForm({
        sourceIncident: inc?.id || "",
        standard: "VDA 6.3",
        severity: inc?.severity || "s1",
        auditor: "",
        scheduledDate: d.toISOString().slice(0, 10),
        scope: inc?.description || "",
        email: true,
        inApp: true,
        overrideType: false,
      });
    }
  }, [isOpen, prefillId]);

  if (!isOpen) return null;

  const auditorType = LPA_ROUTING[form.severity]?.type || "internal";
  const at = auditorTypeStyle(auditorType);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = () => {
    createLpa.mutate({
      sourceIncident: form.sourceIncident,
      standard: form.standard,
      severity: form.severity,
      auditorType,
      auditor: form.auditor,
      scheduledDate: form.scheduledDate,
      scope: form.scope,
      status: "pending",
      requestedBy: "G. Castro",
      notifications: [
        ...(form.email ? [{ channel: "Email", target: form.auditor, at: new Date().toISOString() }] : []),
        ...(form.inApp ? [{ channel: "In-app", target: form.auditor, at: new Date().toISOString() }] : []),
      ],
    });
    addToast({
      type: "info",
      title: "LPA Audit Request sent",
      body: `${at.label} auditor notified`,
    });
    setTimeout(() => {
      addToast({
        type: "info",
        title: "Pre-audit logbook generated",
        body: "Incident + 8D + timeline attached",
      });
    }, 800);
    closeModal();
  };

  return (
    <>
      <div className="fixed inset-0 z-[700] backdrop-enter" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }} onClick={closeModal} />
      <div className="fixed z-[701] flex items-end sm:items-center justify-center pointer-events-none" style={{ inset: 0 }}>
        <div
          className="modal-enter pointer-events-auto flex flex-col"
          style={{ background: "#111420", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, width: "min(540px, 100vw)", maxHeight: "90dvh", boxShadow: "0 30px 70px rgba(0,0,0,0.6)" }}
          data-testid="new-lpa-modal"
        >
          <header className="relative px-5 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
            <div style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: 16, color: "#f0f2f8" }}>
              New LPA Audit Request
            </div>
            <button onClick={closeModal} className="absolute right-3 top-3 flex items-center justify-center rounded-md hover:bg-white/5" style={{ width: 28, height: 28, border: "1px solid rgba(255,255,255,0.1)", color: "#9aa1b3" }}>
              <X size={14} />
            </button>
          </header>
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            <div>
              <Label>Source Incident</Label>
              <select value={form.sourceIncident} onChange={(e) => setField("sourceIncident", e.target.value)} style={inputStyle} className="mt-1">
                <option value="" style={{ background: "#0d0f18" }}>— None —</option>
                {incidents.map((i) => <option key={i.id} value={i.id} style={{ background: "#0d0f18" }}>{i.id} — {i.title}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Audit Standard</Label>
                <select value={form.standard} onChange={(e) => setField("standard", e.target.value)} style={inputStyle} className="mt-1" data-testid="lpa-standard-select">
                  {LPA_STANDARDS.map((s) => <option key={s} value={s} style={{ background: "#0d0f18" }}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label>Severity Level</Label>
                <select value={form.severity} onChange={(e) => setField("severity", e.target.value)} style={inputStyle} className="mt-1">
                  <option value="s1" style={{ background: "#0d0f18" }}>S1 — Critical</option>
                  <option value="s2" style={{ background: "#0d0f18" }}>S2 — High</option>
                  <option value="s3" style={{ background: "#0d0f18" }}>S3 — Medium</option>
                  <option value="s4" style={{ background: "#0d0f18" }}>S4 — Low</option>
                </select>
              </div>
            </div>
            <div className="rounded-md p-2.5" style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.12)" }}>
              <div className="flex items-center gap-2">
                <Label>Auditor Type</Label>
                <span
                  style={{
                    background: at.bg, border: `1px solid ${at.border}`, color: at.color,
                    fontFamily: "'DM Mono', monospace", fontSize: 9, padding: "2px 7px", borderRadius: 3, letterSpacing: "1px",
                  }}
                  data-testid="lpa-auditor-type-badge"
                >
                  {at.label}
                </span>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0", marginLeft: "auto" }}>
                  auto-routed from {form.severity.toUpperCase()} + {form.standard}
                </span>
              </div>
            </div>
            <div>
              <Label>Assigned Auditor</Label>
              <input style={inputStyle} className="mt-1" placeholder="name + email — full directory in Phase 3" value={form.auditor} onChange={(e) => setField("auditor", e.target.value)} />
            </div>
            <div>
              <Label>Scheduled Date</Label>
              <input type="date" style={inputStyle} className="mt-1" value={form.scheduledDate} onChange={(e) => setField("scheduledDate", e.target.value)} />
            </div>
            <div>
              <Label>Audit Scope</Label>
              <textarea rows={3} style={{ ...inputStyle, resize: "vertical" }} className="mt-1" value={form.scope} onChange={(e) => setField("scope", e.target.value)} />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2" style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#cfd1d8" }}>
                <input type="checkbox" checked={form.email} onChange={(e) => setField("email", e.target.checked)} /> Email notification
              </label>
              <label className="flex items-center gap-2" style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#cfd1d8" }}>
                <input type="checkbox" checked={form.inApp} onChange={(e) => setField("inApp", e.target.checked)} /> In-app notification
              </label>
            </div>
          </div>
          <footer className="flex items-center gap-2 px-5 py-3" style={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}>
            <button onClick={closeModal} className="px-4 py-2 rounded-md" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.1)", color: "#9aa1b3", fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 600, fontSize: 12 }}>Cancel</button>
            <div className="flex-1" />
            <button onClick={handleSubmit} className="px-4 py-2 rounded-md" style={{ background: "#8e44ad", color: "#fff", fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 12 }} data-testid="submit-lpa-btn">Send Audit Request</button>
          </footer>
        </div>
      </div>
    </>
  );
};
